import React, { useMemo, useState } from 'react';
import { Box, Checkbox, Grid, IconButton, InputAdornment, TextField, Typography, alpha, useTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import Modal from '@Components/Modal';
import SbButton from '@Components/Button';

export interface ExportColumnSelectionModalProps {
  open: boolean;
  onClose: () => void;
  exportLabel: string;
  columns: string[];
  selectedColumns: string[];
  onSelectedColumnsChange: (columns: string[]) => void;
  onExport: () => void;
  isExporting: boolean;
}

export default function ExportColumnSelectionModal({
  open,
  onClose,
  exportLabel,
  columns,
  selectedColumns,
  onSelectedColumnsChange,
  onExport,
  isExporting,
}: ExportColumnSelectionModalProps) {
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = useState<string>('');

  const filteredColumns = useMemo(() => {
    if (!searchTerm.trim()) return columns;
    const term = searchTerm.toLowerCase();
    return columns.filter(col => col.toLowerCase().includes(term));
  }, [columns, searchTerm]);

  const allSelected = columns.length > 0 && selectedColumns.length === columns.length;

  const handleToggleColumn = (col: string) => {
    if (selectedColumns.includes(col)) {
      onSelectedColumnsChange(selectedColumns.filter(c => c !== col));
    } else {
      onSelectedColumnsChange([...selectedColumns, col]);
    }
  };

  const handleSelectAll = () => {
    onSelectedColumnsChange([...columns]);
  };

  const handleClearAll = () => {
    onSelectedColumnsChange([]);
  };

  return (
    <Modal
      open={open}
      onClose={isExporting ? undefined : onClose}
      maxWidth="md"
      dialogContainerSx={{
        padding: { xs: 2, sm: 2.5 },
        width: { xs: '95vw', sm: '650px', md: '750px' },
        maxWidth: '100%',
        maxHeight: '85vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          height: '100%',
          maxHeight: { xs: 'calc(85vh - 32px)', sm: 'calc(85vh - 40px)' },
          overflow: 'hidden',
        }}
      >
        {/* Fixed Header & Controls Area */}
        <Box sx={{ flexShrink: 0 }}>
          {/* Top Title & Close */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              pb: 1.5,
              borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
            }}
          >
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.text.primary, fontSize: '1.25rem' }}>
                Select Export Columns
              </Typography>
              {exportLabel && (
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontWeight: 500,
                    mt: 0.25,
                    display: 'inline-block',
                  }}
                >
                  {exportLabel.endsWith('Export') || exportLabel.endsWith('Report')
                    ? exportLabel
                    : `${exportLabel} Export`}
                </Typography>
              )}
            </Box>

            <IconButton
              onClick={onClose}
              disabled={isExporting}
              size="small"
              sx={{
                color: '#9ca3af',
                '&:hover': { color: '#374151', backgroundColor: 'rgba(0, 0, 0, 0.04)' },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Controls: Search & Select All / Clear All */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'stretch', sm: 'center' },
              gap: 1.5,
              py: 1.5,
            }}
          >
            <TextField
              size="small"
              placeholder="Search columns..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              disabled={isExporting}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: '#9ca3af' }} />
                  </InputAdornment>
                ),
                sx: {
                  borderRadius: '8px',
                  backgroundColor: '#f9fafb',
                  fontSize: '0.875rem',
                  '& fieldset': { borderColor: '#e5e7eb' },
                  '&:hover fieldset': { borderColor: '#d1d5db' },
                },
              }}
              sx={{ width: { xs: '100%', sm: '240px' } }}
            />

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 600 }}>
                Selected: {selectedColumns.length} / {columns.length}
              </Typography>

              <Box sx={{ display: 'flex', gap: 1 }}>
                <SbButton
                  variant="outlined"
                  onClick={handleSelectAll}
                  disabled={isExporting || allSelected}
                  sx={{
                    height: '32px !important',
                    fontSize: '0.8rem !important',
                    borderRadius: '6px !important',
                    padding: '4px 10px !important',
                    textTransform: 'none',
                  }}
                >
                  Select All
                </SbButton>
                <SbButton
                  variant="outlined"
                  onClick={handleClearAll}
                  disabled={isExporting || selectedColumns.length === 0}
                  sx={{
                    height: '32px !important',
                    fontSize: '0.8rem !important',
                    borderRadius: '6px !important',
                    padding: '4px 10px !important',
                    textTransform: 'none',
                  }}
                >
                  Clear All
                </SbButton>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Scrollable Middle Content Area */}
        <Box
          sx={{
            flex: '1 1 auto',
            overflowY: 'auto',
            minHeight: 0,
            p: 1.5,
            borderRadius: '8px',
            backgroundColor: '#f9fafb',
            border: '1px solid #e5e7eb',
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0, 0, 0, 0.15)',
              borderRadius: '4px',
            },
          }}
        >
          {filteredColumns.length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: '#9ca3af' }}>
                No columns match &quot;{searchTerm}&quot;
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={1}>
              {filteredColumns.map(col => {
                const isChecked = selectedColumns.includes(col);
                return (
                  <Grid item xs={12} sm={6} md={4} key={col}>
                    <Box
                      onClick={() => !isExporting && handleToggleColumn(col)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        p: '4px 8px',
                        borderRadius: '6px',
                        cursor: isExporting ? 'default' : 'pointer',
                        backgroundColor: isChecked ? alpha(theme.palette.primary.main, 0.08) : '#ffffff',
                        border: isChecked
                          ? `1px solid ${alpha(theme.palette.primary.main, 0.35)}`
                          : '1px solid #e5e7eb',
                        transition: 'all 0.15s ease-in-out',
                        '&:hover': {
                          backgroundColor: isChecked
                            ? alpha(theme.palette.primary.main, 0.14)
                            : alpha(theme.palette.primary.main, 0.04),
                          borderColor: isChecked
                            ? alpha(theme.palette.primary.main, 0.55)
                            : alpha(theme.palette.primary.main, 0.25),
                        },
                      }}
                    >
                      <Checkbox
                        checked={isChecked}
                        disabled={isExporting}
                        size="small"
                        sx={{
                          p: 0.5,
                          mr: 0.75,
                          color: theme.palette.text.secondary,
                          '&.Mui-checked': {
                            color: theme.palette.primary.main,
                          },
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.08),
                          },
                          '&.Mui-checked:hover': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.12),
                          },
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: '0.85rem',
                          fontWeight: isChecked ? 600 : 400,
                          color: isChecked
                            ? theme.palette.primary.dark || theme.palette.primary.main
                            : theme.palette.text.primary,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          userSelect: 'none',
                        }}
                        title={col}
                      >
                        {col}
                      </Typography>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Box>

        {/* Fixed Footer Area */}
        <Box
          sx={{
            flexShrink: 0,
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 1.5,
            pt: 2,
            mt: 1,
            borderTop: '1px solid rgba(0, 0, 0, 0.08)',
          }}
        >
          <SbButton
            variant="outlined"
            onClick={onClose}
            disabled={isExporting}
            sx={{
              borderRadius: '8px !important',
              px: 3,
              height: '40px !important',
              textTransform: 'none',
            }}
          >
            Cancel
          </SbButton>

          <SbButton
            variant="contained"
            onClick={onExport}
            loading={isExporting}
            disabled={selectedColumns.length === 0 || isExporting}
            sx={{
              borderRadius: '8px !important',
              px: 3.5,
              height: '40px !important',
              fontWeight: 600,
              textTransform: 'none',
            }}
          >
            Export
          </SbButton>
        </Box>
      </Box>
    </Modal>
  );
}
